import { useEffect, useMemo, useState } from 'react';
import { plannerGroceryMeals, plannerTotalMissingCount } from '../../utils/mealPlanHelpers.js';
import './planner.css';

/**
 * Five canonical grocery buckets, identical to the Pantry/Shop screens
 * (see backend/services/receipt_parser.py BUCKET_*).
 *
 * Display order is rail-specific: Fruits & Veg first because that's the bulk
 * of weekly perishables, then Proteins, etc.
 */
const CATEGORY_ORDER = [
  'fruits_vegetables',
  'proteins',
  'dairy',
  'spices',
  'staples',
];

const CATEGORY_LABEL = {
  proteins: 'Proteins',
  fruits_vegetables: 'Fruits & Veg',
  dairy: 'Dairy',
  spices: 'Spices',
  staples: 'Pantry & Staples',
};

const VALID_CATEGORIES = new Set(CATEGORY_ORDER);

function normalizeMissing(entry) {
  if (!entry) return null;
  if (typeof entry === 'string') {
    const n = entry.trim();
    if (!n) return null;
    return { name: n, category: 'staples' };
  }
  if (typeof entry === 'object') {
    const name = typeof entry.name === 'string' ? entry.name.trim() : '';
    if (!name) return null;
    const cat =
      typeof entry.category === 'string' && VALID_CATEGORIES.has(entry.category)
        ? entry.category
        : 'staples';
    return { name, category: cat };
  }
  return null;
}

function normalizeAllIngredient(entry) {
  if (!entry) return null;
  if (typeof entry === 'string') {
    const n = entry.trim();
    if (!n) return null;
    return { name: n, category: 'staples' };
  }
  if (typeof entry === 'object') {
    const name = typeof entry.name === 'string' ? entry.name.trim() : '';
    if (!name) return null;
    const cat =
      typeof entry.category === 'string' && VALID_CATEGORIES.has(entry.category)
        ? entry.category
        : 'staples';
    return { name, category: cat };
  }
  return null;
}

/*
 * CompactGroceryRail — sticky right-rail on desktop, inline section on mobile.
 *
 * Aggregates meal.missing[] from assigned meals, grouped by the same five buckets
 * Pantry/Shop use (proteins, fruits_vegetables, dairy, spices, staples). The
 * backend categorizes each missing ingredient via the shared inventory helper
 * so labels match across screens.
 *
 * Rows are pre-checked. Unchecking excludes that name from the Shop add CTA
 * (opt-out selection). Hidden/truncated missing lines stay included unless their
 * name is unchecked on a visible row.
 *
 * "Heavy" mode (totalMissing >= 20): eyebrow flips orange, CTA becomes btn-sharp.
 *
 * Props:
 *   assigned: meal[] — only assigned meals contribute to the list
 *   filter: 'missing' | 'all'
 *   onFilter: (filter) => void
 *   onBuildGroceryList: optional (opts?: { excludeItemNames?: string[] }) => void
 *   buildBusy: optional boolean
 *   showHeader: optional boolean — false when a parent already renders the section title
 *   showUtilityActions: optional boolean
 */
export default function CompactGroceryRail({
  assigned,
  filter,
  onFilter,
  onBuildGroceryList = null,
  buildBusy = false,
  showHeader = true,
  showUtilityActions = true,
  allIngredientsLoading = false,
  allIngredientsError = false,
  onRetryAllIngredients = null,
}) {
  const groceryMeals = useMemo(() => plannerGroceryMeals(assigned), [assigned]);
  const items = useMemo(() => {
    const next = [];
    groceryMeals.forEach((meal) => {
      const mid = meal.id ?? meal.planItemId ?? meal.recipeId ?? 'meal';
      const sourceLines = filter === 'all' ? (meal.allIngredients || []) : (meal.missing || []);
      const normalizeLine = filter === 'all' ? normalizeAllIngredient : normalizeMissing;
      sourceLines.forEach((entry, mi) => {
        const norm = normalizeLine(entry);
        if (!norm) return;
        next.push({
          id: `${mid}-${filter}-${mi}`,
          name: norm.name,
          category: norm.category,
          for: meal.name,
        });
      });
    });
    return next;
  }, [groceryMeals, filter]);

  const itemIdsKey = useMemo(() => items.map((item) => item.id).join('\0'), [items]);
  const [checked, setChecked] = useState(() => new Set());

  useEffect(() => {
    if (!itemIdsKey) {
      setChecked(new Set());
      return;
    }
    setChecked(new Set(itemIdsKey.split('\0')));
  }, [itemIdsKey]);
  const toggleCheck = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const groups = {};
  items.forEach((item) => {
    const key = item.category in CATEGORY_LABEL ? item.category : 'staples';
    (groups[key] = groups[key] || []).push(item);
  });

  const listedMissing = items.length;
  const totalMissing = plannerTotalMissingCount(groceryMeals);
  const hiddenMissing = Math.max(0, totalMissing - listedMissing);
  const heavy = totalMissing >= 20;
  const checkedCount = items.filter((item) => checked.has(item.id)).length;
  const excludeItemNames = items
    .filter((item) => !checked.has(item.id))
    .map((item) => item.name);
  const nothingSelected = onBuildGroceryList && totalMissing > 0 && checkedCount === 0;

  const filterPills = (
    <div className="grocery-filter-pills" role="group" aria-label="Filter missing items">
      {[
        { id: 'missing', label: `Missing (${totalMissing})` },
        { id: 'all', label: 'All' },
      ].map((opt) => (
        <button
          key={opt.id}
          type="button"
          className={`grocery-filter-pill${filter === opt.id ? ' active' : ''}`}
          onClick={() => onFilter(opt.id)}
          aria-pressed={filter === opt.id}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  return (
    <aside
      className={`grocery-rail${!showHeader ? ' grocery-rail--embedded' : ''}`}
      aria-label="Missing items for this week"
    >
      {showHeader ? (
        <div className="grocery-rail-header">
          <div className={`grocery-rail-eyebrow ${heavy ? 'heavy' : 'normal'}`}>
            {heavy ? 'First big shop' : 'For this week'}
          </div>
          <div className="planner-title-rail">Missing items</div>
          <div className="planner-grocery-meta">
            {totalMissing} missing · {groceryMeals.length} meal{groceryMeals.length !== 1 ? 's' : ''}
          </div>
          {filterPills}
        </div>
      ) : (
        <div className="grocery-rail-toolbar">{filterPills}</div>
      )}

      <div className="grocery-rail-list">
        {totalMissing === 0 && filter === 'missing' ? (
          <p className="planner-muted-copy">
            Nothing to shop — your week is fully cookable.
          </p>
        ) : items.length === 0 && filter === 'all' && allIngredientsLoading ? (
          <p className="planner-muted-copy">Loading ingredients…</p>
        ) : items.length === 0 && filter === 'all' && allIngredientsError ? (
          <p className="planner-muted-copy">
            Could not load ingredients.{' '}
            <button
              type="button"
              className="button secondary"
              onClick={() => onRetryAllIngredients?.()}
            >
              Try again
            </button>
          </p>
        ) : items.length === 0 && filter === 'all' ? (
          <p className="planner-muted-copy">
            No lines yet — assign meals or add missing items to Shop from the plan.
          </p>
        ) : (
          <>
          {filter === 'missing' && hiddenMissing > 0 ? (
            <p className="planner-muted-copy planner-muted-copy-spaced">
              Showing {listedMissing} of {totalMissing} missing — open the recipe for the full list.
            </p>
          ) : null}
          {CATEGORY_ORDER.filter((key) => groups[key]?.length > 0).map((key) => (
            <div key={key} className="grocery-aisle">
              <div className="grocery-aisle-label">{CATEGORY_LABEL[key]}</div>
              {groups[key].map((item) => {
                const isChecked = checked.has(item.id);
                return (
                  <div
                    key={item.id}
                    className={`grocery-item planner-grocery-item-clickable${!isChecked ? ' have' : ''}`}
                    onClick={() => toggleCheck(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleCheck(item.id);
                      }
                    }}
                    role="checkbox"
                    aria-checked={isChecked}
                    aria-label={`${item.name} for ${item.for}`}
                    tabIndex={0}
                  >
                    <span
                      className={`planner-check${isChecked ? ' checked' : ''}`}
                      aria-hidden="true"
                    >
                      {isChecked && '✓'}
                    </span>
                    <div>
                      <div className="grocery-item-name">{item.name}</div>
                      <div className="grocery-item-for">for {item.for}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          </>
        )}
      </div>

      <div className="grocery-rail-footer">
        <button
          type="button"
          className={`planner-btn ${heavy ? 'btn-sharp' : 'btn-primary'} planner-rail-btn`}
          disabled={!!buildBusy || (onBuildGroceryList && (totalMissing === 0 || nothingSelected))}
          onClick={() => {
            if (!onBuildGroceryList) return;
            onBuildGroceryList(
              excludeItemNames.length > 0 ? { excludeItemNames } : undefined,
            );
          }}
        >
          {buildBusy
            ? 'Adding…'
            : onBuildGroceryList
              ? (totalMissing === 0
                ? 'Nothing missing'
                : nothingSelected
                  ? 'Select items to add'
                  : excludeItemNames.length > 0
                    ? `Add ${checkedCount} to Shop →`
                    : 'Add missing items to Shop →')
              : (heavy ? 'Start the big shop →' : 'Open in-store list →')}
        </button>
        {showUtilityActions && (
          <div className="grocery-rail-secondary-row">
            <button
              type="button"
              className="planner-btn btn-secondary planner-rail-btn-secondary"
              disabled
              title="Coming soon"
            >
              Instacart
            </button>
            <button
              type="button"
              className="planner-btn btn-secondary planner-rail-btn-secondary"
              disabled
              title="Coming soon"
            >
              Print
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
